import { el, styled } from './svg';

/* Every sprite stroke rides `vector-effect: non-scaling-stroke`, so
   `stroke-width` is interpreted in CSS pixels regardless of board scale.
   STYLEGUIDE §4-5's "one hand, same weight at every zoom" is literally what
   non-scaling-stroke encodes: at a 40px cell, 1 board unit is 0.4 CSS px, so
   unscaled 1-2 unit widths render sub-pixel and blur out. Pixel widths hold
   their line at any board size. */
const FRAME =
  'stroke:var(--frame);fill:none;stroke-linecap:round;stroke-linejoin:round;vector-effect:non-scaling-stroke;';
/* Lamps and lit glass. The keyline is nothing in the base palette and an
   `ink` casing under high contrast, where the pale structures put amber at
   1.4:1 against its own housing — the ratio ran out, so the drawing separates
   the two instead (STYLEGUIDE §12). */
const GLASS =
  'stroke:var(--glass-case);stroke-width:var(--glass-case-w);vector-effect:non-scaling-stroke;';
const DOT = 'fill:var(--dot);' + GLASS;
/** The insulator's styled radius: `--dot-k` swells it on energize (board.css
    .tower). The `r` attribute at each call site stays as the fallback for
    engines without CSS geometry properties. */
const dotR = (r: number) => `r:calc(${r}px * var(--dot-k, 1));`;

/** Structure stroke width riding the weight multiplier: board.css sets
    `--stroke-k` per sprite family so the whole hand can gain or lose line
    weight without touching a drawing. Callers keep the plain width as an
    attribute fallback for engines without calc() here. */
const sw = (w: number) => `stroke-width:calc(${w}px * var(--stroke-k, 1));`;

/** The warm point under an energized insulator tip: two underlay circles in
    `--dot-glow` (transparent while dark, amber at low alpha when energized —
    board.css), the outer one fainter so the point falls off like a glow
    instead of printing a disk. Both ride the tip's own `--dot-k` swell. */
function tipGlow(g: SVGGElement, cx: number, cy: number, r: number): void {
  const R1 = +(r * 1.6).toFixed(2);
  const R2 = +(r * 2.6).toFixed(2);
  styled(
    g,
    'circle',
    { cx, cy, r: R2 },
    'fill:var(--dot-glow, transparent);opacity:.35;' + dotR(R2),
  );
  styled(g, 'circle', { cx, cy, r: R1 }, 'fill:var(--dot-glow, transparent);' + dotR(R1));
}

/** The beacon's own small bloom (STYLEGUIDE §5): two rings under the lamp,
    filter-free like every board glow. `--beacon-glow-k` zeroes them under
    the accessibility modes (tokens.css), where light is not ours to spend. */
function beaconGlow(g: SVGGElement, cx: number, cy: number, r: number, color: string): void {
  styled(
    g,
    'circle',
    { cx, cy, r: +(r * 3).toFixed(2) },
    `fill:${color};opacity:calc(0.16 * var(--beacon-glow-k, 1))`,
  );
  styled(
    g,
    'circle',
    { cx, cy, r: +(r * 1.9).toFixed(2) },
    `fill:${color};opacity:calc(0.34 * var(--beacon-glow-k, 1))`,
  );
}

/** Tower footings — a steel-family tone kept subtle enough that four footings
    per tower don't compete with the silhouette. The tone lives in tokens.css
    with the rest of the palette, because high contrast lifts land past the
    base value and the footing would sink into the ground it stands on. */
const FOOTING = 'var(--footing)';

/** How many drawn variants each terrain tile has — maps drift, they don't tile. */
export const TERRAIN_VARIANTS = 3;

/** Drawn variants of the ground-flora pieces — same reason: a scatter drifts. */
export const GRASS_VARIANTS = 3;
export const PEBBLE_VARIANTS = 2;

/**
 * How much bigger the human-scale sprites (plant, house, suspension tower)
 * draw than their raw path coords. Everything inside a `scale(SPRITE_SCALE)`
 * wrapper `<g>` scales together — fills and paths — while `non-scaling-stroke`
 * keeps every stroke at its target CSS-pixel weight, so the line hand stays
 * one hand across sizes (STYLEGUIDE §5).
 *
 * The cat-face tower has its own sx/sy in the draw call, so its callers
 * multiply the base scale in directly.
 *
 * 1.4 is the ceiling that keeps a top-row crossing giant's peak inside the
 * current 52-unit sky band. The scaling is anchored at sprite origin (0,0),
 * so ground contact drifts down but stays inside the cell; the top overflows
 * upward, which is what gives the tall towers their isometric feel.
 */
export const SPRITE_SCALE = 1.4;

/** Sprite ids referenced by `<use href="#…">`. Terrain ids take a variant suffix. */
export const SPRITE = {
  standard: '#pylonShape',
  standardSide: '#pylonShapeSide',
  junction: '#junctionShape',
  junctionSide: '#junctionShapeSide',
  crossing: '#crossingShape',
  crossingSide: '#crossingShapeSide',
  deadend: '#deadendShape',
  deadendFaceL: '#deadendFaceL',
  deadendFaceR: '#deadendFaceR',
  plant: '#plantShape',
  house: '#houseShape',
  houseGlow: '#houseGlowShape',
  mtn: '#mtnShape',
  water: '#waterShape',
  grass: '#grassShape',
  reed: '#reedShape',
  pebble: '#pebbleShape',
  scrub: '#scrubShape',
  bush: '#bushShape',
  boulder: '#boulderShape',
  tree: '#treeShape',
  bird: '#birdShape',
  birdFly: '#birdFlyShape',
  hawk: '#hawkShape',
  beaconLamp: '#beaconLampShape',
} as const;

/** The crossing giant's scale against raw cat-face units (see buildDefs). */
const CROSSING_SCALE_FACTOR = 1.02;

/**
 * Where the crossing giant's warning lamps sit, relative to the tower use's
 * own (x, y) anchor. board.ts overlays the blinking `#beaconLampShape` there
 * as a real document element — a CSS animation on a lamp inside the tower
 * def never reaches the `<use>` clones, so a blink drawn in the def renders
 * as a steady light (see the beacon branch in drawCatFaceTower).
 */
export function crossingBeaconSeats(side: boolean): { x: number; y: number }[] {
  const s = SPRITE_SCALE * CROSSING_SCALE_FACTOR;
  const yAt = (y: number) => +((y - 34) * s + 34).toFixed(2);
  if (side) return [{ x: 0, y: yAt(-49.4) }];
  const x = +(EAR_TIP_X * s).toFixed(2);
  const y = yAt(EAR_TIP_Y - 0.5);
  return [
    { x: -x, y },
    { x, y },
  ];
}

export interface Tower500Options {
  /** Horizontal scale against the base junction size. */
  sx: number;
  /** Vertical scale against the base junction size. */
  sy: number;
  /** Board-unit y of the ground line. */
  ground: number;
  steel: string;
  dot: string;
  dotR: number;
  /** Hazard-band the legs — the river-crossing giant. */
  bands?: boolean;
  /** Blinking aircraft-warning beacons on the shield-wire tips. */
  beacon?: boolean;
  /** Beacons hold steady when motion is reduced. */
  animate?: boolean;
}

/* ---------------------------------------------------------------------------
   The 500 kV cat-face tower — the heavy end of the tower family, worn by
   junctions and river crossings (STYLEGUIDE.md §5).

   Vertical stations in base units, ground at y = 34. Horizontal stations are
   half-widths from the centre line. Everything below is expressed against
   these, so the silhouette can be retuned without hunting through paths.

   The shape is a real cat-face pylon: pointed shield-wire ears, a shield-like
   head with a wishbone carrying the centre phase, deep triangular crossarms
   trussed rather than solid, a pinched waist beneath them, and a body that
   splits into two splayed legs.

   The head is left deliberately empty — outline, ears and wishbone, nothing
   else. It is the tower's one open shape, and lattice inside it closes up into
   a grey mass at the size these are actually drawn.
   --------------------------------------------------------------------------- */
const EAR_TIP_Y = -48;
const HEAD_TOP_Y = -42;
const DOT_C_Y = -31;
const HEAD_WIDE_Y = -24;
const HEAD_FOOT_Y = -14;
const ARM_TIP_Y = -13;
const WAIST_Y = -6;
const DOT_OUT_Y = -3;
const SPLIT_Y = 14;
const GROUND_Y = 34;

const EAR_TIP_X = 12.5;
const EAR_ROOT_X = 7;
const HEAD_TOP_X = 13.7;
const HEAD_WIDE_X = 16;
const HEAD_FOOT_X = 10;
const ARM_TIP_X = 30;
const DOT_OUT_X = 18;
const WAIST_X = 5;
const SPLIT_X = 7.5;
const FOOT_X = 15;

/** Half-width of the body between the waist and the leg split. */
const bodyX = (y: number) => WAIST_X + ((SPLIT_X - WAIST_X) * (y - WAIST_Y)) / (SPLIT_Y - WAIST_Y);

/** Half-width of a leg between the split and the ground. */
const legX = (y: number) => SPLIT_X + ((FOOT_X - SPLIT_X) * (y - SPLIT_Y)) / (GROUND_Y - SPLIT_Y);

/** A point on the arm's top chord, `t` from root to tip. */
const armTop = (t: number): [number, number] => [
  HEAD_FOOT_X + (ARM_TIP_X - HEAD_FOOT_X) * t,
  HEAD_FOOT_Y + (ARM_TIP_Y - HEAD_FOOT_Y) * t,
];

/** A point on the arm's lower chord, `u` from the waist out to the tip. */
const armLow = (u: number): [number, number] => [
  WAIST_X + (ARM_TIP_X - WAIST_X) * u,
  WAIST_Y + (ARM_TIP_Y - WAIST_Y) * u,
];

/** The drawing helpers for the cat-face coordinate system. */
function catHelpers(g: SVGGElement, o: Tower500Options) {
  const X = (x: number) => +(x * o.sx).toFixed(2);
  const Y = (y: number) => +((y - 34) * o.sy + o.ground).toFixed(2);
  const path = (pts: number[][], w: number, col?: string) =>
    styled(
      g,
      'path',
      {
        d: pts.map((p, i) => (i ? 'L' : 'M') + X(p[0]) + ' ' + Y(p[1])).join(' '),
        'stroke-width': w,
      },
      `stroke:${col || o.steel};${sw(w)}fill:none;stroke-linecap:round;stroke-linejoin:round;vector-effect:non-scaling-stroke`,
    );
  const dot = (x: number, y: number, r: number) => {
    tipGlow(g, X(x), Y(y), r);
    styled(g, 'circle', { cx: X(x), cy: Y(y), r }, `fill:${o.dot};` + GLASS + dotR(r));
  };
  return { X, Y, path, dot };
}

// Hazard paint, night-muted: brick red and pale grey, not signal colors. The
// tones live in tokens.css so forced colors can claim them (STYLEGUIDE §12).
const HAZARD_RED = 'var(--hazard)';
const HAZARD_WHT = 'var(--matte)';

/**
 * Two thin banding ticks high on the suspension legs — aviation paint at
 * tick dose (STYLEGUIDE §5): equipment, not state, static in both states,
 * and the first decoration to drop if the tower role ladder ever reads
 * diluted. The full-leg banding stays the crossing giant's alone.
 */
function bandTicks(g: SVGGElement): void {
  const legX = (y: number) => +(4.5 + (8.5 * (y + 20)) / 54).toFixed(2);
  for (const s of [1, -1]) {
    for (const [y, col] of [
      [-4, HAZARD_RED],
      [2, HAZARD_WHT],
    ] as const) {
      styled(
        g,
        'path',
        { d: `M${+(s * legX(y) - 1.8).toFixed(2)} ${y} h3.6`, 'stroke-width': 1.5 },
        `stroke:${col};${sw(1.5)}opacity:.55;stroke-linecap:round;fill:none;vector-effect:non-scaling-stroke`,
      );
    }
  }
}

/**
 * The heavy family's shared base — the waist pinching in, the body widening
 * to the leg split, the splayed legs (hazard-banded on the crossing giant)
 * and the lattice panels down both. The cat-face and the dead-end stand on
 * this one base at one height: in the field the two share a line, and role
 * recognition lives entirely in the head each carries.
 */
function drawCatBase(g: SVGGElement, o: Tower500Options): void {
  const { Y, path } = catHelpers(g, o);
  for (const s of [1, -1]) {
    const m = (a: number[][]) => a.map(([x, y]) => [x * s, y]);
    path(
      m([
        [WAIST_X, WAIST_Y],
        [SPLIT_X, SPLIT_Y],
      ]),
      1.8,
    );
    if (o.bands) {
      for (let i = 0; i < 4; i++) {
        const y0 = SPLIT_Y + ((GROUND_Y - SPLIT_Y) * i) / 4;
        const y1 = SPLIT_Y + ((GROUND_Y - SPLIT_Y) * (i + 1)) / 4;
        path(
          m([
            [legX(y0), y0],
            [legX(y1), y1],
          ]),
          3,
          i % 2 ? HAZARD_WHT : HAZARD_RED,
        );
      }
    } else {
      path(
        m([
          [SPLIT_X, SPLIT_Y],
          [FOOT_X, GROUND_Y],
        ]),
        1.8,
      );
    }
    el(
      'rect',
      {
        x: (s * FOOT_X * o.sx - 3.5).toFixed(1),
        y: Y(GROUND_Y - 1),
        width: 7,
        height: 4,
        rx: 1,
        fill: FOOTING,
      },
      g,
    );
  }

  // body: rungs and X panels between the waist and the leg split
  let prev = WAIST_Y;
  for (const y of [0.7, 7.3, SPLIT_Y]) {
    path(
      [
        [-bodyX(y), y],
        [bodyX(y), y],
      ],
      1,
    );
    path(
      [
        [-bodyX(prev), prev],
        [bodyX(y), y],
      ],
      1,
    );
    path(
      [
        [bodyX(prev), prev],
        [-bodyX(y), y],
      ],
      1,
    );
    prev = y;
  }
  // the same, carried on down between the splayed legs
  prev = SPLIT_Y;
  for (const y of [20.7, 27.3]) {
    path(
      [
        [-legX(y), y],
        [legX(y), y],
      ],
      1,
    );
    path(
      [
        [-legX(prev), prev],
        [legX(y), y],
      ],
      1,
    );
    path(
      [
        [legX(prev), prev],
        [-legX(y), y],
      ],
      1,
    );
    prev = y;
  }
}

/**
 * Draw the tower into `g`.
 *
 * The base design is in junction-size game units; `sx`/`sy` rescale it for the
 * standard tower (smaller) and the river-crossing giant, which also gets
 * hazard-banded legs and beacons.
 *
 * Stroke widths are CSS pixels (see FRAME on non-scaling-stroke): structural
 * outlines around 1.8 px, truss/lattice at 1 px, hazard bands at 3 px.
 */
export function drawCatFaceTower(g: SVGGElement, o: Tower500Options): void {
  const { X, Y, path, dot } = catHelpers(g, o);
  drawCatBase(g, o);

  for (const s of [1, -1]) {
    const m = (a: number[][]) => a.map(([x, y]) => [x * s, y]);

    // ear: a shield-wire peak standing on the head's top chord
    path(
      m([
        [EAR_ROOT_X, HEAD_TOP_Y],
        [EAR_TIP_X, EAR_TIP_Y],
        [HEAD_TOP_X, HEAD_TOP_Y],
      ]),
      1.5,
    );
    path(
      m([
        [8.6, -43.9],
        [12.9, -45.2],
      ]),
      1,
    );

    // head: top corner out to the cheek, then in to the crossarm root
    path(
      m([
        [HEAD_TOP_X, HEAD_TOP_Y],
        [HEAD_WIDE_X, HEAD_WIDE_Y],
        [HEAD_FOOT_X, HEAD_FOOT_Y],
      ]),
      1.8,
    );

    // wishbone carrying the centre phase down to its insulator
    path(
      m([
        [9.5, HEAD_TOP_Y],
        [0, DOT_C_Y],
      ]),
      1.8,
    );

    // crossarm: a deep triangle, top chord and lower chord meeting at the tip
    path(m([armTop(0), armTop(1)]), 1.8);
    path(m([armLow(0), armLow(1)]), 1.5);
    // ...trussed rather than solid, so it reads as steel at any size
    path(
      m([
        armTop(0),
        armLow(0.25),
        armTop(0.25),
        armLow(0.5),
        armTop(0.5),
        armLow(0.75),
        armTop(0.75),
        armLow(1),
      ]),
      1,
    );

    // outer phase on a V-string slung under the arm
    path(m([armLow(0.4), [DOT_OUT_X, DOT_OUT_Y], armLow(0.64)]), 1);

    // waist: the head's foot pinches in to meet the shared base
    path(
      m([
        [HEAD_FOOT_X, HEAD_FOOT_Y],
        [WAIST_X, WAIST_Y],
      ]),
      1.8,
    );

    // shield-wire tip: steel dot, or a blinking beacon on the giant
    if (o.beacon) {
      // A real aircraft-warning light — the one red the board is allowed —
      // with its own small bloom. The blinking form is NOT drawn here: a CSS
      // animation inside a def never reaches the `<use>` clones, so a blink
      // spelled in the def renders as a steady lamp. board.ts overlays a
      // real `#beaconLampShape` per giant instead (crossingBeaconSeats
      // hands it these coordinates). Reduced motion keeps the lamp steady,
      // and steady is something a def can hold.
      if (!o.animate) {
        const lamp = el('g', {}, g);
        beaconGlow(lamp, X(EAR_TIP_X * s), Y(EAR_TIP_Y - 0.5), 2.6, 'var(--alert)');
        styled(
          lamp,
          'circle',
          { cx: X(EAR_TIP_X * s), cy: Y(EAR_TIP_Y - 0.5), r: 2.6 },
          'fill:var(--alert)',
        );
      }
    } else {
      dot(EAR_TIP_X * s, EAR_TIP_Y + 0.3, o.dotR * 0.62);
      // a still aviation lamp on each ear — equipment, not state (§5): one
      // lamp per seat the head offers, because a lone lamp on a symmetric
      // pair of peaks reads as burned out. The blink stays the giant's, and
      // a lamp is light: each carries its bloom.
      beaconGlow(g, X(EAR_TIP_X * s), Y(EAR_TIP_Y - 2.2), 1.4, 'var(--beacon)');
      styled(
        g,
        'circle',
        { cx: X(EAR_TIP_X * s), cy: Y(EAR_TIP_Y - 2.2), r: 1.4 },
        'fill:var(--beacon)',
      );
    }
  }

  // the head's top chord, spanning its full width with an ear at each end
  path(
    [
      [-HEAD_TOP_X, HEAD_TOP_Y],
      [HEAD_TOP_X, HEAD_TOP_Y],
    ],
    1.8,
  );

  // the three phase conductors (delta): one on the wishbone, two under the arms
  dot(0, DOT_C_Y, o.dotR);
  dot(DOT_OUT_X, DOT_OUT_Y, o.dotR);
  dot(-DOT_OUT_X, DOT_OUT_Y, o.dotR);
}

/**
 * The everyday suspension tower — the same drawing as the app icon and the
 * wordmark glyph, at board scale: one T crossarm, a shield-wire peak, an
 * X-braced tapering body.
 *
 * Real grids mix tower families by job, and so does the board: suspension
 * towers carry the ordinary spans, the cat-face takes junctions, the banded
 * giant takes the river. The variety is information — a tower's silhouette
 * tells you what it is doing — and the family stays one hand: same strokes,
 * same steel, same amber insulators.
 */
function drawSuspensionTower(g: SVGGElement): void {
  const seg = (d: string, w: number) => styled(g, 'path', { d, 'stroke-width': w }, FRAME + sw(w));
  const dot = (x: number, y: number) => {
    tipGlow(g, x, y, 2.7);
    styled(g, 'circle', { cx: x, cy: y, r: 2.7 }, DOT + dotR(2.7));
  };

  // legs, tapering from the footings to the crossarm
  seg('M-13 34 L-4.5 -20', 1.8);
  seg('M13 34 L4.5 -20', 1.8);

  // banding ticks high on each leg — equipment, not state (STYLEGUIDE §5):
  // static in both states, and the first decoration to go if the tower
  // role ladder ever reads diluted
  bandTicks(g);

  // crossarm: a shallow truss — the lower chord rises to meet the top chord
  // at the tips, so the two bars read as one deep arm
  seg('M-20 -20 H20', 1.8);
  seg('M-20 -20 L-15 -14.6 H15 L20 -20', 1);

  // peak carrying the shield wire
  seg('M-4.5 -20 L0 -27.5 L4.5 -20', 1.5);

  // rungs and X panels down the body
  const hw = (y: number) => +(4.5 + (8.5 * (y + 20)) / 54).toFixed(2);
  const rungs = [-6, 8, 22];
  for (const y of rungs) seg(`M${-hw(y)} ${y} H${hw(y)}`, 1);
  for (let i = 0; i < rungs.length - 1; i++) {
    const [a, b] = [rungs[i], rungs[i + 1]];
    seg(`M${-hw(a)} ${a} L${hw(b)} ${b}`, 1);
    seg(`M${hw(a)} ${a} L${-hw(b)} ${b}`, 1);
  }

  // footings
  for (const s of [1, -1]) {
    el('rect', { x: s * 13 - 3.5, y: 33, width: 7, height: 4, rx: 1, fill: FOOTING }, g);
  }

  // insulators: one on each arm tip, one on the peak
  dot(-17.5, -21.3);
  dot(0, -28.2);
  dot(17.5, -21.3);
}

/**
 * The suspension tower in profile — the same tower as the front view, seen
 * edge-on: a square lattice keeps its taper from the side, so the legs and
 * body panels are the front drawing's own, while the wide crossarm becomes
 * a short truss seen end-on under the unchanged peak. The conductors sit
 * where a real side view puts them: the centre phase high on the peak, the
 * two arm phases close together at arm height — near and far tips peeking
 * past the mast with a small stagger for depth. Conductor points mirror
 * SIDE_CONDUCTORS in wires.ts (÷ SPRITE_SCALE); every height matches the
 * front view, so a tower turning with its line never changes size.
 */
function drawSuspensionTowerSide(g: SVGGElement): void {
  const seg = (d: string, w: number) => styled(g, 'path', { d, 'stroke-width': w }, FRAME + sw(w));
  const dot = (x: number, y: number) => {
    tipGlow(g, x, y, 2.7);
    styled(g, 'circle', { cx: x, cy: y, r: 2.7 }, DOT + dotR(2.7));
  };

  // legs: the front taper exactly — the footprint is square
  seg('M-13 34 L-4.5 -20', 1.8);
  seg('M13 34 L4.5 -20', 1.8);

  // the front view's banding ticks — the tower never changes dress turning
  bandTicks(g);

  // the crossarm end-on: it points at the camera, so it reads as one narrow
  // centered truss — top chord over lower chord — never a stub to either side
  seg('M-3.5 -20 H3.5', 1.5);
  seg('M-3.5 -20 L-2 -14.6 H2 L3.5 -20', 1);

  // peak carrying the shield wire, unchanged from the front
  seg('M-4.5 -20 L0 -27.5 L4.5 -20', 1.5);

  // both outer phases hang in line at the centre, one bead behind the
  // other; the third rides the peak as it does in the front view
  seg('M0 -14.6 L0 -9', 1);
  tipGlow(g, 0, -12.5, 2);
  styled(g, 'circle', { cx: 0, cy: -12.5, r: 2 }, DOT + dotR(2));
  tipGlow(g, 0, -9, 2);
  styled(g, 'circle', { cx: 0, cy: -9, r: 2 }, DOT + dotR(2));
  dot(0, -28.2);

  // rungs and X panels down the body — the front view's own
  const hw = (y: number) => +(4.5 + (8.5 * (y + 20)) / 54).toFixed(2);
  const rungs = [-6, 8, 22];
  for (const y of rungs) seg(`M${-hw(y)} ${y} H${hw(y)}`, 1);
  for (let i = 0; i < rungs.length - 1; i++) {
    const [a, b] = [rungs[i], rungs[i + 1]];
    seg(`M${-hw(a)} ${a} L${hw(b)} ${b}`, 1);
    seg(`M${hw(a)} ${a} L${-hw(b)} ${b}`, 1);
  }

  // footings
  for (const s of [1, -1]) {
    el('rect', { x: s * 13 - 3.5, y: 33, width: 7, height: 4, rx: 1, fill: FOOTING }, g);
  }
}

/**
 * The cat-face family in profile, for junctions and crossing giants on
 * mostly east-west lines — the silhouette the real tower cuts when
 * photographed from beside its right-of-way.
 *
 * It is the front view's own silhouette seen at the tower's depth rather
 * than its width, and the stations say so: the base is the front drawing's
 * exactly, because the footprint is square, and above it the mast pinches
 * to the same waist and swells again into a head. The swell is what makes
 * this a cat-face — a shaft running straight from a wide base to a single
 * spike is the Eiffel silhouette, and the tower stopped reading as its own
 * front view the moment it had no head. The arms all but disappear end-on
 * (they point at the camera) but their depth does not: a short stub either
 * side at arm height, its lower chords converging on the strings, which is
 * the horizontal break the mast otherwise never gets.
 *
 * The two ears merge into one peak seen end-on — the beacon's seat on the
 * giant — and it stands on the head's full width rather than rising out of
 * a needle. The conductors sit as the photograph has them: the centre
 * phase high in the head window at the front view's own wishbone height,
 * the outer pair on strings at arm height, one bead behind the other.
 * Hazard bands wrap the splayed legs on the crossing, at the front view's
 * own stations. Conductor points mirror SIDE_CONDUCTORS in wires.ts
 * (÷ the sx/sy scale); every height matches the front view, so the tower
 * never changes size when it turns with its line.
 */
function drawCatFaceTowerSide(g: SVGGElement, o: Tower500Options): void {
  const X = (x: number) => +(x * o.sx).toFixed(2);
  const Y = (y: number) => +((y - 34) * o.sy + o.ground).toFixed(2);
  const seg = (d: string, w: number, col?: string) =>
    styled(
      g,
      'path',
      { d, 'stroke-width': w },
      `stroke:${col || o.steel};${sw(w)}fill:none;stroke-linecap:round;stroke-linejoin:round;vector-effect:non-scaling-stroke`,
    );
  const line = (x1: number, y1: number, x2: number, y2: number, w: number, col?: string) =>
    seg(`M${X(x1)} ${Y(y1)} L${X(x2)} ${Y(y2)}`, w, col);
  const dot = (x: number, y: number, r: number) => {
    tipGlow(g, X(x), Y(y), r);
    styled(g, 'circle', { cx: X(x), cy: Y(y), r }, `fill:${o.dot};` + GLASS + dotR(r));
  };

  const RED = 'var(--hazard)';
  const WHT = 'var(--matte)';

  /**
   * The silhouette in stations, ground upward: footings, leg split and
   * waist are the front view's own numbers (the footprint is square), and
   * the three above them are the head at its depth — the pinch under it and
   * the swell through it are what separate this tower from a lattice
   * needle.
   */
  const STATIONS: [number, number][] = [
    [34, 15], // footings
    [14, 7.5], // leg split
    [-6, 5], // waist — the narrowest point, as in front
    [-14, 6], // head foot
    [-26, 7.2], // the head at its deepest
    [-42, 6], // head top, where the ears stand
  ];
  /** Half-width at a height, read off the station table. */
  const hw = (y: number): number => {
    for (let i = 0; i < STATIONS.length - 1; i++) {
      const [y0, w0] = STATIONS[i];
      const [y1, w1] = STATIONS[i + 1];
      if (y <= y0 && y >= y1) return +(w0 + ((w1 - w0) * (y - y0)) / (y1 - y0)).toFixed(2);
    }
    return y > 34 ? 15 : 6;
  };

  // the two flanks, station to station — hazard-banded down the splayed
  // legs on the crossing giant, exactly where the front view bands them
  for (const s of [1, -1]) {
    for (let i = 0; i < STATIONS.length - 1; i++) {
      const [y0, w0] = STATIONS[i];
      const [y1, w1] = STATIONS[i + 1];
      if (o.bands && y0 === 34) {
        for (let b = 0; b < 4; b++) {
          const a0 = y0 + ((y1 - y0) * b) / 4;
          const a1 = y0 + ((y1 - y0) * (b + 1)) / 4;
          line(s * hw(a0), a0, s * hw(a1), a1, 3, b % 2 ? WHT : RED);
        }
      } else {
        line(s * w0, y0, s * w1, y1, 1.8);
      }
    }
  }

  // the two ears merge seen end-on, and what they merge into is a short
  // truncated peak, not a nose cone: the flat between the shoulders is the
  // beacon's seat on the giant, and the tower stops being a rocket
  seg(`M${X(-6)} ${Y(-42)} L${X(-2.6)} ${Y(-48.4)} L${X(2.6)} ${Y(-48.4)} L${X(6)} ${Y(-42)}`, 1.5);

  // shield-wire tip: steel dot, or the giant's blinking beacon. As in the
  // front view, only the steady reduced-motion lamp is drawn in the def —
  // the blinking one is board.ts's overlay (crossingBeaconSeats).
  if (o.beacon) {
    if (!o.animate) {
      const lamp = el('g', {}, g);
      beaconGlow(lamp, X(0), Y(-49.4), 2.6, 'var(--alert)');
      styled(lamp, 'circle', { cx: X(0), cy: Y(-49.4), r: 2.6 }, 'fill:var(--alert)');
    }
  } else {
    dot(0, -49.3, o.dotR * 0.62);
    // the ear pair's still lamps (§5), merged end-on: in profile the near
    // lamp hides the far one, so the seat shows a single light
    beaconGlow(g, X(0), Y(-51.8), 1.4, 'var(--beacon)');
    styled(g, 'circle', { cx: X(0), cy: Y(-51.8), r: 1.4 }, 'fill:var(--beacon)');
  }

  // the crossarms end-on: the pair points at the camera, so what shows is
  // the truss's own depth — a short trussed stub either side, shouldering
  // out past the head above it and tapering back into the mast below. A
  // bare bar here read as a brim; the arm needs its X to read as steel.
  // Both outer phases hang in line beneath it, one bead behind the other.
  line(-8.5, -14, 8.5, -14, 1.5);
  for (const s of [1, -1]) line(s * 8.5, -14, s * 5.2, -7, 1.5);
  line(-8.5, -14, 5.2, -7, 1);
  line(8.5, -14, -5.2, -7, 1);
  line(-5.2, -7, 5.2, -7, 1);
  line(0, -7, 0, -1.4, 1);
  dot(0, -4.6, o.dotR * 0.72);
  dot(0, -1.4, o.dotR * 0.72);

  // the centre phase, hung in the head window at the wishbone's own height
  line(0, -38, 0, -31, 1);
  dot(0, -31, o.dotR);

  // lattice: rungs and X panels up the base, and plain rungs across the
  // head — the head stays the tower's one open shape here as it is in front
  const rungs = [30, 22, 14, 5, -5];
  for (const y of rungs) line(-hw(y), y, hw(y), y, 1);
  for (let i = 0; i < rungs.length - 1; i++) {
    const [a, b] = [rungs[i], rungs[i + 1]];
    line(-hw(a), a, hw(b), b, 1);
    line(hw(a), a, -hw(b), b, 1);
  }
  for (const y of [-20, -28, -36]) line(-hw(y), y, hw(y), y, 1);

  // footings
  for (const s of [1, -1]) {
    el(
      'rect',
      { x: (s * 15 * o.sx - 3.5).toFixed(1), y: Y(33), width: 7, height: 4, rx: 1, fill: FOOTING },
      g,
    );
  }
}

/**
 * The dead-end anchor — the strain tower that turns or terminates a line,
 * drawn from the real one: it stands on the cat-face's own base at the
 * cat-face's own height, because in the field the two share a line, and
 * everything that says "the line anchors here" lives in the head. A mast
 * rises from the shared waist to a boxed hat wider at its top than its
 * base, under a shallow shield-wire gable.
 *
 * The arms are NOT drawn here. On a strain tower the arm is the anchor
 * fixture itself, so a face that holds no wire carries no arm at all: each
 * side's arms, braces, jumper loops and terminal dots live in the per-face
 * overlays (`deadendFaceL`/`deadendFaceR`) the board adds only where a
 * span actually lands.
 */
function drawDeadEndTower(g: SVGGElement, o: Tower500Options): void {
  const { X, Y, path } = catHelpers(g, o);
  drawCatBase(g, o);

  // one still aviation lamp at the gable peak — equipment, not state (§5)
  beaconGlow(g, X(0), Y(-48.8), 1.4, 'var(--beacon)');
  styled(g, 'circle', { cx: X(0), cy: Y(-48.8), r: 1.4 }, 'fill:var(--beacon)');

  /** Mast half-width between the shared waist and the head's base. */
  const mastX = (y: number) => WAIST_X + ((3.8 - WAIST_X) * (y - WAIST_Y)) / (-28 - WAIST_Y);

  for (const s of [1, -1]) {
    const m = (a: number[][]) => a.map(([x, y]) => [x * s, y]);
    // the mast, rising from the waist to the head, then the hat's flare
    path(
      m([
        [WAIST_X, WAIST_Y],
        [3.8, -28],
        [7, -42],
      ]),
      1.8,
    );
  }

  // the hat: top beam, bottom tie, X-brace, and the shield-wire gable
  path(
    [
      [-7, -42],
      [7, -42],
    ],
    1.5,
  );
  path(
    [
      [-3.8, -28],
      [3.8, -28],
    ],
    1,
  );
  path(
    [
      [-3.8, -28],
      [7, -42],
    ],
    1,
  );
  path(
    [
      [3.8, -28],
      [-7, -42],
    ],
    1,
  );
  path(
    [
      [-7, -42],
      [0, -47],
      [7, -42],
    ],
    1.5,
  );

  // lattice up the mast
  let prev = WAIST_Y;
  for (const y of [-13, -21, -28]) {
    path(
      [
        [-mastX(y), y],
        [mastX(y), y],
      ],
      1,
    );
    path(
      [
        [-mastX(prev), prev],
        [mastX(y), y],
      ],
      1,
    );
    path(
      [
        [mastX(prev), prev],
        [-mastX(y), y],
      ],
      1,
    );
    prev = y;
  }
}

/**
 * One face's strain hardware, mounted by the board only where wire lands:
 * the short upper arm off the head's flank, the long main arm at the
 * cat-face's own conductor height, their braces, the jumper loops slung
 * under each tip, and the terminal dots the wires actually hang from.
 * Geometry mirrors DEADEND_TIERS in wires.ts.
 */
function drawDeadEndFace(g: SVGGElement, o: Tower500Options, s: 1 | -1): void {
  const { X, Y, path, dot } = catHelpers(g, o);
  const q = (x1: number, y1: number, cxx: number, cyy: number, x2: number, y2: number) =>
    styled(
      g,
      'path',
      { d: `M${X(x1)} ${Y(y1)} Q ${X(cxx)} ${Y(cyy)} ${X(x2)} ${Y(y2)}`, 'stroke-width': 1 },
      `stroke:${o.steel};${sw(1)}fill:none;stroke-linecap:round;vector-effect:non-scaling-stroke`,
    );

  // upper arm, off the head's flank
  path(
    [
      [s * 4.7, -32],
      [s * 11, -32],
    ],
    1.5,
  );
  path(
    [
      [s * 11, -32],
      [s * 4.1, -29],
    ],
    1,
  );
  // main arm, at the cat-face's own conductor height
  path(
    [
      [s * 4.6, -13],
      [s * 16, -13],
    ],
    1.5,
  );
  path(
    [
      [s * 16, -13],
      [s * 4.8, -9],
    ],
    1,
  );

  // the jumper loops, slung under each arm tip — the dead-end's signature
  q(s * 11, -32, s * 8.5, -25, s * 6, -32);
  q(s * 16, -13, s * 12.5, -5, s * 9, -13);

  // the terminals: one on the upper arm, the main arm's tip and inner string
  dot(s * 11, -32, o.dotR);
  dot(s * 16, -13, o.dotR);
  dot(s * 9, -13, o.dotR);
}

/**
 * Mountains drawn in the same hand as everything else on the board.
 *
 * Topo contour rings were tried and cut: the board's language is pictorial —
 * towers, houses and the plant are all little drawings of the things
 * themselves — so a lone cartographic convention read as noise, and it asked
 * for map literacy the game has no right to assume. A jagged peak silhouette
 * is legible to everyone.
 *
 * Each variant is a ridge outline over a translucent highland fill (blocked
 * terrain is *toned*, exactly as water is — the breath of tone is the
 * signal), with facet strokes running down the faces. The moonlit summit
 * caps were cut with the second edition: a translucent line-drawn peak is
 * the direction (STYLEGUIDE §4), and the caps were the one element that
 * read as rendered rather than drawn.
 */
const MTN_VARIANTS: { ridge: string; facets: string[] }[] = [
  {
    // twin peaks, the main one right of centre
    ridge: 'M-46 30 L-18 -14 L-6 -1 L12 -31 L46 30 Z',
    facets: ['M12 -31 L4 -4', 'M-18 -14 L-23 8', 'M24 -12 L31 6'],
  },
  {
    // one dominant peak, a foothill on its east shoulder
    ridge: 'M-50 30 L-14 -33 L0 -10 L16 -20 L50 30 Z',
    facets: ['M-14 -33 L-19 -2', 'M16 -20 L11 4'],
  },
  {
    // a three-peak ridge
    ridge: 'M-48 30 L-26 -10 L-14 -1 L2 -28 L18 -6 L30 -13 L48 30 Z',
    facets: ['M2 -28 L-3 -2', 'M-26 -10 L-30 8', 'M30 -13 L26 6'],
  },
];

/**
 * Two or three short curved ripple strokes per water tile — not tildes. Sits
 * in the same subliminal tier as the rest of the ambient world layer
 * (smoke, glint, plant breath): thin stroke, low opacity, quiet enough that
 * the water reads as water without the ripples ever competing with the wire
 * hand.
 */
const WATER_VARIANTS: { d: string; opacity?: number }[][] = [
  [
    { d: 'M-34 -16 q8 -7 16 0 t16 0', opacity: 0.32 },
    { d: 'M-20 12 q8 -7 16 0 t16 0', opacity: 0.24 },
    { d: 'M-42 32 q7 -6 14 0', opacity: 0.22 },
  ],
  [
    // the first stroke crosses the tile edge on purpose — water doesn't tile
    { d: 'M-52 -8 q8 -7 16 0 t16 0', opacity: 0.32 },
    { d: 'M-8 18 q8 -7 16 0 t16 0', opacity: 0.22 },
  ],
  [
    { d: 'M-30 -30 q7 -6 14 0 t14 0', opacity: 0.32 },
    { d: 'M-38 6 q8 -7 16 0 t16 0', opacity: 0.32 },
    { d: 'M-6 34 q7 -6 14 0', opacity: 0.22 },
  ],
];

/**
 * Build the sprite library once per board.
 *
 * The tower family, one hand, three jobs: the suspension tower for ordinary
 * spans, the cat-face for junctions, the hazard-banded giant for river
 * crossings. Silhouette = role, at a glance.
 */
export function buildDefs(board: SVGSVGElement, animate: boolean): void {
  const defs = el('defs', {}, board);

  // --- standard: the suspension tower, the wordmark's own drawing.
  //     Wrapped in scale(SPRITE_SCALE) so it fills more of its cell without
  //     hunting each coord (attach points scale with it — see wires.ts). ---
  const py = el('g', { id: 'pylonShape' }, defs);
  const pyInner = el('g', { transform: `scale(${SPRITE_SCALE})` }, py);
  drawSuspensionTower(pyInner);

  // --- dead-end: the anchor that turns a line sharper than 45°. It stands
  //     on the cat-face's own base at the cat-face's own height — in the
  //     field the two share a line — and role recognition lives in the
  //     head: the hat, the double arms, the jumper loops. ---
  const de = el('g', { id: 'deadendShape' }, defs);
  drawDeadEndTower(de, {
    sx: SPRITE_SCALE * 0.9,
    sy: SPRITE_SCALE * 0.9,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.2,
  });

  //     The anchor's arms ride per-side overlays: a dead-end terminates a
  //     span on the face turned toward it, and only a face holding wire
  //     shows hardware — a bare face carries no arm at all, just the mast.
  for (const [id, side] of [
    ['deadendFaceL', -1],
    ['deadendFaceR', 1],
  ] as const) {
    const dOuter = el('g', { id }, defs);
    drawDeadEndFace(
      dOuter,
      {
        sx: SPRITE_SCALE * 0.9,
        sy: SPRITE_SCALE * 0.9,
        ground: 34,
        steel: 'var(--frame)',
        dot: 'var(--dot)',
        dotR: 3.2,
      },
      side,
    );
  }

  // --- junction: for towers carrying three or more lines. Uniformly 10%
  //     smaller than the base SPRITE_SCALE so the cat-face reads as a
  //     mid-sized structure — dwarfed by the crossing giant, distinctly
  //     bigger than the suspension pylon (STYLEGUIDE §5). ---
  const jn = el('g', { id: 'junctionShape' }, defs);
  drawCatFaceTower(jn, {
    sx: SPRITE_SCALE * 0.9,
    sy: SPRITE_SCALE * 0.9,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.2,
  });

  // --- river-crossing giant: uniformly 2% bigger than a junction on both
  //     axes — proportional silhouette, no vertical stretch, and small
  //     enough that it fits comfortably in its cell with room to spare.
  //     Role recognition lives on the hazard bands and twin beacons; the
  //     size premium is just a whisper on top (STYLEGUIDE §5). ---
  const cr = el('g', { id: 'crossingShape' }, defs);
  drawCatFaceTower(cr, {
    sx: SPRITE_SCALE * CROSSING_SCALE_FACTOR,
    sy: SPRITE_SCALE * CROSSING_SCALE_FACTOR,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.4,
    bands: true,
    beacon: true,
    animate,
  });

  // --- profile views: a tower faces its line, so a mostly east-west run
  //     mounts these instead of the head-on shapes (see towerFacings in
  //     wires.ts). Same family, same hand, one job each. ---
  const pys = el('g', { id: 'pylonShapeSide' }, defs);
  const pysInner = el('g', { transform: `scale(${SPRITE_SCALE})` }, pys);
  drawSuspensionTowerSide(pysInner);

  const jns = el('g', { id: 'junctionShapeSide' }, defs);
  drawCatFaceTowerSide(jns, {
    sx: SPRITE_SCALE * 0.9,
    sy: SPRITE_SCALE * 0.9,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.2,
  });

  const crs = el('g', { id: 'crossingShapeSide' }, defs);
  drawCatFaceTowerSide(crs, {
    sx: SPRITE_SCALE * CROSSING_SCALE_FACTOR,
    sy: SPRITE_SCALE * CROSSING_SCALE_FACTOR,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.4,
    bands: true,
    beacon: true,
    animate,
  });

  // --- the giant's blinking warning lamp, its own def: board.ts overlays
  //     one per crossing giant as a real document element, because a CSS
  //     animation on a lamp inside the tower def never reaches the `<use>`
  //     clones (see the beacon branch in drawCatFaceTower). ---
  const bl = el('g', { id: 'beaconLampShape' }, defs);
  beaconGlow(bl, 0, 0, 2.6, 'var(--alert)');
  styled(bl, 'circle', { cx: 0, cy: 0, r: 2.6 }, 'fill:var(--alert)');

  // --- power plant: a steel-drawn machine hall with the one-line-diagram
  //     generator symbol on its face, and a small steady indicator lamp.
  //     A machine that's on, not a bonfire. Wrapped in scale(SPRITE_SCALE)
  //     so path coords stay legible while the sprite fills its cell. ---
  const plOuter = el('g', { id: 'plantShape' }, defs);
  const pl = el('g', { transform: `scale(${SPRITE_SCALE})` }, plOuter);
  // Body strokes ride --frame (not a literal steel) so the state ramp and
  // the halo twin theme the whole drawing through one property.
  styled(
    pl,
    'rect',
    { x: -26, y: -4, width: 52, height: 30, rx: 2, 'stroke-width': 1.5 },
    `fill:var(--structure);stroke:var(--frame);${sw(1.5)}vector-effect:non-scaling-stroke`,
  );
  styled(
    pl,
    'rect',
    { x: -20, y: -20, width: 9, height: 18, rx: 1, 'stroke-width': 1 },
    `fill:var(--structure-hi);stroke:var(--frame);${sw(1)}vector-effect:non-scaling-stroke`,
  );
  styled(
    pl,
    'rect',
    { x: -7, y: -14, width: 9, height: 12, rx: 1, 'stroke-width': 1 },
    `fill:var(--structure-hi);stroke:var(--frame);${sw(1)}vector-effect:non-scaling-stroke`,
  );
  // the takeoff gantry the grid actually hangs from: a mast (its bare top
  // reads as the shield-wire spike) and a beam carrying the three phases
  styled(pl, 'path', { d: 'M12 -34 L12 -4', 'stroke-width': 1.8 }, FRAME + sw(1.8));
  styled(pl, 'path', { d: 'M2 -28 L22 -28', 'stroke-width': 1.5 }, FRAME + sw(1.5));
  tipGlow(pl, 4, -28, 2.5);
  styled(pl, 'circle', { cx: 4, cy: -28, r: 2.5 }, DOT);
  tipGlow(pl, 12, -28, 2.5);
  styled(pl, 'circle', { cx: 12, cy: -28, r: 2.5 }, DOT);
  tipGlow(pl, 20, -28, 2.5);
  styled(pl, 'circle', { cx: 20, cy: -28, r: 2.5 }, DOT);
  // generator: a circle with a bolt — the machine says what it makes
  styled(pl, 'circle', { cx: -6, cy: 11, r: 8, 'stroke-width': 1.5 }, FRAME + sw(1.5));
  styled(
    pl,
    'path',
    { d: 'M-4.4 5.6 L-9.4 11.6 h2.8 l-1.4 4.8 5-6 h-2.8 z', 'stroke-width': 1 },
    FRAME + sw(1) + 'stroke-linejoin:round',
  );
  // the indicator lamp: on, steady, small
  tipGlow(pl, 19, 3, 2);
  styled(pl, 'circle', { cx: 19, cy: 3, r: 2 }, DOT);

  // --- house: a dark silhouette in a thin steel line; windows carry the state ---
  const hoOuter = el('g', { id: 'houseShape' }, defs);
  const ho = el('g', { transform: `scale(${SPRITE_SCALE})` }, hoOuter);
  // The ground pool: window light landing on the earth, drawn first so the
  // silhouette stands on it. Two stacked ellipses fake the falloff without a
  // filter — the tipGlow recipe at house scale. `--pool` is transparent
  // until the house lights (board.css).
  styled(
    ho,
    'ellipse',
    { cx: 0, cy: 22, rx: 27, ry: 7.5 },
    'fill:var(--pool, transparent);opacity:.35',
  );
  styled(ho, 'ellipse', { cx: 0, cy: 22, rx: 17, ry: 4.8 }, 'fill:var(--pool, transparent)');
  styled(
    ho,
    'rect',
    { x: 10, y: -16, width: 7, height: 12, rx: 1, 'stroke-width': 1 },
    `fill:var(--roof);stroke:var(--frame);${sw(1)}vector-effect:non-scaling-stroke`,
  );
  styled(
    ho,
    'path',
    { d: 'M0 -20 L0 -30', 'stroke-width': 1.5 },
    `stroke:var(--frame);${sw(1.5)}fill:none;vector-effect:non-scaling-stroke`,
  );
  styled(ho, 'circle', { cx: 0, cy: -30, r: 2.6 }, 'fill:var(--win);' + GLASS);
  styled(
    ho,
    'path',
    { d: 'M-25 0 L0 -21 L25 0 Z', 'stroke-width': 1.2 },
    `fill:var(--roof);stroke:var(--frame);${sw(1.2)}stroke-linejoin:round;vector-effect:non-scaling-stroke`,
  );
  styled(
    ho,
    'rect',
    { x: -19, y: 0, width: 38, height: 24, rx: 1.5, 'stroke-width': 1.2 },
    `fill:var(--body);stroke:var(--frame);${sw(1.2)}vector-effect:non-scaling-stroke`,
  );
  styled(ho, 'rect', { x: -13, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win);' + GLASS);
  styled(ho, 'rect', { x: 4, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win);' + GLASS);

  // --- the windows alone, as an overlay layer: the only glow on the board
  //     belongs to lit windows, so the glow rides a sprite that *is* only
  //     windows (a filter on the full house would halo the whole silhouette) ---
  const hgOuter = el('g', { id: 'houseGlowShape' }, defs);
  const hg = el('g', { transform: `scale(${SPRITE_SCALE})` }, hgOuter);
  styled(hg, 'circle', { cx: 0, cy: -30, r: 2.6 }, 'fill:var(--win);' + GLASS);
  styled(hg, 'rect', { x: -13, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win);' + GLASS);
  styled(hg, 'rect', { x: 4, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win);' + GLASS);

  // --- mountains: translucent line-drawn peaks (STYLEGUIDE §4). The paths
  //     already touch the cell edges horizontally, so the scaling here is
  //     vertical only — peaks stand taller so the range reads at the same
  //     scale as the sprites around it, without overflowing the cell's
  //     horizontal footprint. The fill opacity is a token: high contrast
  //     re-opaques the peaks, because the toned fill is the blocked-terrain
  //     signal and the floor outranks the look (§12). ---
  MTN_VARIANTS.forEach((v, i) => {
    const mtOuter = el('g', { id: 'mtnShape' + i }, defs);
    const mt = el('g', { transform: `scale(1, ${SPRITE_SCALE})` }, mtOuter);
    styled(
      mt,
      'path',
      { d: v.ridge },
      'fill:var(--highland);fill-opacity:var(--mtn-fill-o, 1);stroke:var(--contour);stroke-width:var(--terrain-stroke);stroke-linejoin:round;vector-effect:non-scaling-stroke',
    );
    for (const f of v.facets) {
      styled(
        mt,
        'path',
        { d: f },
        'stroke:var(--contour);stroke-width:var(--facet-stroke);fill:none;stroke-linecap:round;opacity:.8;vector-effect:non-scaling-stroke',
      );
    }
  });

  // --- water: a few quiet ripples, held still. The water body itself is no
  //     longer a tile: board.ts fills the shore contour (shore.ts) in one
  //     path, so the fill follows the coves and the wobble; this sprite is
  //     only the per-tile texture riding on it. The ripples do not drift —
  //     a drift slow enough to be tasteful is one nobody can see, and it
  //     would cost the board a frame pipeline for as long as it ran
  //     (STYLEGUIDE §4, §12). The glints are what make the water alive, and
  //     they are light. ---
  WATER_VARIANTS.forEach((strokes, i) => {
    const wa = el('g', { id: 'waterShape' + i }, defs);
    const rip = el('g', {}, wa);
    for (const s of strokes) {
      styled(
        rip,
        'path',
        { d: s.d, ...(s.opacity !== undefined ? { opacity: s.opacity } : {}) },
        // Thinner than the wire hand and mostly translucent — one voice in
        // the ambient layer, never a shout (STYLEGUIDE §8 item 6).
        'stroke:var(--ripple);stroke-width:var(--ripple-stroke);fill:none;stroke-linecap:round;vector-effect:non-scaling-stroke',
      );
    }
  });

  // --- ground flora (STYLEGUIDE §4): terrain art, not ambient life — the
  //     flora module lays out a sparse seeded scatter of these. One hand
  //     with the ripples: 0.75px non-scaling strokes, round caps, no fills —
  //     toned fill is the blocked-terrain signal, so the land field under a
  //     tuft stays untouched. Every symbol is anchored at its ground point
  //     (0,0), strokes rising into negative y. ---
  const FLORA =
    'stroke:var(--flora);fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-width:0.75;vector-effect:non-scaling-stroke;';

  //     Blades root at separate points along the ground: a fan out of one
  //     origin reads as a crow's foot or an arrowhead at board scale, and a
  //     tuft is a clump, not a symbol.
  const GRASS_BLADES: { d: string; opacity: number }[][] = [
    [
      { d: 'M-2.5 0 Q-3 -6 -5.5 -11', opacity: 0.7 },
      { d: 'M0.5 0 Q0.8 -7 1 -13', opacity: 0.8 },
      { d: 'M2.5 0 Q3.5 -6 5.5 -10', opacity: 0.65 },
    ],
    [
      { d: 'M-2 0 Q-3.5 -5 -6.5 -8', opacity: 0.7 },
      { d: 'M0.5 0 Q-0.5 -7 -2 -12', opacity: 0.8 },
      { d: 'M2 0 Q3 -4 4.5 -6', opacity: 0.65 },
    ],
    [
      { d: 'M-3 0 Q-5 -4 -8.5 -6', opacity: 0.65 },
      { d: 'M-1 0 Q-1.8 -6 -2.5 -8', opacity: 0.75 },
      { d: 'M1 0 Q1.8 -6 3.5 -8', opacity: 0.8 },
      { d: 'M3 0 Q5 -4 8.5 -5', opacity: 0.65 },
    ],
  ];
  GRASS_BLADES.forEach((blades, i) => {
    const gr = el('g', { id: 'grassShape' + i }, defs);
    for (const b of blades) styled(gr, 'path', { d: b.d, opacity: b.opacity }, FLORA);
  });

  //     reeds: taller stems with a shared lean, sized to straddle a
  //     shoreline. No seed-head dots — a dot in this hand reads as an
  //     insulator terminal.
  const rd = el('g', { id: 'reedShape' }, defs);
  styled(rd, 'path', { d: 'M0 0 Q-0.5 -10 -2 -18', opacity: 0.7 }, FLORA);
  styled(rd, 'path', { d: 'M2.5 0 Q3 -11 3.5 -20', opacity: 0.75 }, FLORA);
  styled(rd, 'path', { d: 'M-2.5 0 Q-3.5 -9 -5.5 -15', opacity: 0.65 }, FLORA);

  //     pebbles: outline ellipses hugging the ground — the quietest piece,
  //     outlines only so no stone ever reads as a filled dot terminal.
  //     Stones keep daylight between them: two outline ellipses touching
  //     read as an infinity sign, not a pair of rocks.
  const PEBBLES: { cx: number; cy: number; rx: number; ry: number }[][] = [
    [
      { cx: -3.6, cy: -1.6, rx: 2.8, ry: 1.8 },
      { cx: 3.4, cy: -1.3, rx: 2, ry: 1.4 },
    ],
    [
      { cx: -5.2, cy: -1.4, rx: 2.2, ry: 1.6 },
      { cx: 0.5, cy: -1.8, rx: 3, ry: 2 },
      { cx: 6.2, cy: -1.1, rx: 1.6, ry: 1.2 },
    ],
  ];
  PEBBLES.forEach((stones, i) => {
    const pe = el('g', { id: 'pebbleShape' + i }, defs);
    for (const s of stones) styled(pe, 'ellipse', { ...s, opacity: 0.55 }, FLORA);
  });

  //     scrub: one bumpy arc left open along the ground — an unclosed bottom
  //     reads hand-drawn, and closing it would invite a fill.
  const sc = el('g', { id: 'scrubShape' }, defs);
  styled(
    sc,
    'path',
    { d: 'M-7 0 Q-8.5 -6 -4 -8 Q-2 -11 1 -9.5 Q5 -11 6.5 -6 Q9 -3.5 7 0', opacity: 0.62 },
    FLORA,
  );
  styled(sc, 'path', { d: 'M0 0 Q0.5 -4 1.5 -7', opacity: 0.62 }, FLORA);

  //     bush: the scrub's idea grown a size up — a low two-lobe shrub, open
  //     along the ground, two branch strokes inside. Legibility comes from
  //     size, never weight: the FLORA stroke stays 0.75 (STYLEGUIDE §4).
  const bu = el('g', { id: 'bushShape' }, defs);
  styled(
    bu,
    'path',
    {
      d: 'M-9 0 Q-11 -7 -6 -10 Q-4 -14 0 -12 Q3 -15 6 -11 Q10 -9 8 -3 Q9 -1 8 0',
      opacity: 0.7,
    },
    FLORA,
  );
  styled(bu, 'path', { d: 'M-1 0 Q-1 -5 -2 -9', opacity: 0.6 }, FLORA);
  styled(bu, 'path', { d: 'M2 0 Q3 -4 5 -7', opacity: 0.6 }, FLORA);

  //     boulder: a closed faceted outline with one facet stroke down the
  //     face — the mountain idiom at hand scale, so a rock reads as the
  //     highland family. Outline only: a filled shape would breach the
  //     toned-fill blocked-terrain signal, and a filled dot reads as a
  //     terminal. A small second stone keeps it from reading as a lone blob.
  const bo = el('g', { id: 'boulderShape' }, defs);
  styled(bo, 'path', { d: 'M-8 0 L-9 -4.5 L-4 -9 L3 -9.5 L8 -5 L7.5 0 Z', opacity: 0.7 }, FLORA);
  styled(bo, 'path', { d: 'M-4 -9 L-2 0', opacity: 0.6 }, FLORA);
  styled(bo, 'path', { d: 'M9.5 0 L9 -2.5 L11.5 -4 L14 -2 L13.5 0 Z', opacity: 0.6 }, FLORA);
  //     the ground line under the pair: at board scale the facets wash out
  //     and two floating blobs read as a cloud — a rock sits, so seat it.
  styled(bo, 'path', { d: 'M-11 0.5 H16', opacity: 0.4 }, FLORA);

  //     tree: a small conifer — canopy outline over a trunk tick, taller
  //     than the bush so it reads as a tree at a glance. Open along the
  //     canopy's base like the scrub: an unclosed bottom reads hand-drawn.
  //     No stroke up the middle: a shaft through stacked chevrons turned
  //     the whole drawing into an upward arrow at board scale. One sagging
  //     branch line inside says conifer instead.
  const tr = el('g', { id: 'treeShape' }, defs);
  styled(tr, 'path', { d: 'M0 0 L0 -3.4', opacity: 0.75 }, FLORA);
  styled(tr, 'path', { d: 'M-4.8 -3.4 L-0.5 -15.5 L4.6 -3.7', opacity: 0.8 }, FLORA);
  styled(tr, 'path', { d: 'M-2.8 -7.4 Q0 -9.2 2.7 -7.6', opacity: 0.5 }, FLORA);

  // --- ambient inhabitants (STYLEGUIDE §5 world layer, §8 item 6): perched
  //     silhouettes drawn in the tower steel. They never glow — the glow
  //     budget stays with circuit — and their motion is a landing tween the
  //     birds module drives per instance, not a def-level SMIL loop. Sized
  //     for readability on a phone: a 900-unit board scales to ~380 CSS px
  //     on the narrowest device, so a bird body of ~15 units is ~6 CSS px —
  //     small but recognisable as a silhouette. ---
  const bd = el('g', { id: 'birdShape' }, defs);
  // Perched, facing +X, with (0,0) at the feet so the bird stands on the
  // wire. The silhouette has a front and a back — breast, head, beak one
  // way; tail the other — because a symmetrical body under a centred head
  // dot is a lozenge, and a lozenge on a wire reads as a bead.
  const BIRD = 'fill:var(--frame)';
  // body: the back arching from the rump up over the shoulder, down the
  // breast and back along the belly
  styled(
    bd,
    'path',
    { d: 'M-6 -4.5 Q-5.5 -10.2 1 -10.2 Q6.2 -9.8 6.6 -5.2 Q6.2 -1 0.8 -0.7 Q-3.5 -0.7 -6 -4.5 Z' },
    BIRD,
  );
  // head over the breast, with a short beak — the end the bird is looking
  styled(bd, 'circle', { cx: 7, cy: -10.6, r: 2.5 }, BIRD);
  styled(bd, 'path', { d: 'M9.2 -11.1 L12.8 -10.1 L9.2 -9.2 Z' }, BIRD);
  // the folded wing, its tip carried back past the rump: the one line that
  // says wing at silhouette size, and the reason the tail below it reads as
  // a tail rather than as the whole rear of the bird
  styled(
    bd,
    'path',
    { d: 'M-1.5 -8.8 Q3 -8.4 4.2 -5.9 Q0 -4 -9.8 -4.5 Q-6 -6.8 -1.5 -8.8 Z' },
    BIRD,
  );
  // tail, swept back and down under the wing
  styled(bd, 'path', { d: 'M-5.4 -5.4 L-13.4 -0.9 L-12.6 1.1 L-4.6 -2 Z' }, BIRD);
  // legs — two ticks down to the wire, so the bird grips it
  styled(
    bd,
    'path',
    { d: 'M-0.6 -0.8 L-0.6 0 M2.6 -0.8 L2.6 0', 'stroke-width': 0.9 },
    'stroke:var(--frame);fill:none;stroke-linecap:round',
  );

  const bf = el('g', { id: 'birdFlyShape' }, defs);
  // In flight, side-on, drawn facing +X around its own body centre — the
  // hawk's rule (its comment next door records what a wingless silhouette
  // on a curve reads as), applied to the small birds: the flight module
  // turns this shape into the tangent, so the beak leads and the wings
  // stay square to the line of travel. Wings caught mid-beat, the near one
  // raised and the far one dropped, so the two read as depth rather than
  // as a butterfly.
  styled(
    bf,
    'path',
    { d: 'M-11.5 1.1 Q-5 -2.6 3 -2.4 Q8 -2.2 10.4 -0.5 Q8 1.7 3 2.2 Q-5 2.6 -11.5 1.1 Z' },
    BIRD,
  );
  styled(bf, 'circle', { cx: 11.2, cy: -1.1, r: 2.3 }, BIRD);
  styled(bf, 'path', { d: 'M13.3 -1.6 L16.8 -0.7 L13.3 0.2 Z' }, BIRD);
  // tail fanned behind
  styled(bf, 'path', { d: 'M-10 -1 L-17.4 -3 L-17.4 2.6 L-10 2.2 Z' }, BIRD);
  // near wing, on the upstroke
  styled(bf, 'path', { d: 'M-1.2 -1.9 L-6.2 -13.2 Q-2.6 -15.2 2 -13.6 L5.2 -1.6 Z' }, BIRD);
  // far wing, dropped — smaller, because it is further away
  styled(bf, 'path', { d: 'M-1.6 1.7 L-4.8 8.4 Q-2.4 9.4 0.6 8.5 L3 1.5 Z' }, BIRD);

  const hk = el('g', { id: 'hawkShape' }, defs);
  // A filled top-down silhouette: hawk soaring with wings held straight out
  // to the sides, fanned tail behind, small head with a hooked beak forward.
  // Drawn facing +X so that `rotate: auto` on the animateMotion carries the
  // beak into the direction of flight and the wings extend perpendicular to
  // it — the previous stroke-only M-shape had its "wings" pointing along
  // the tangent, which read as a bar sliding sideways through the sky.
  styled(
    hk,
    'path',
    {
      d: [
        'M15 0', // beak tip
        'L12 -2', // head top
        'L6 -3', // shoulder
        'L4.5 -3', // wing root leading edge
        'L3 -20', // leading edge sweeping out to the left wingtip
        'Q0 -25 -3 -20', // wingtip curve
        'L-4.5 -3', // trailing edge back to the shoulder
        'L-9 -3', // rear body
        'L-13 -5', // tail fan corner (top)
        'L-15 0', // tail centre — fanned open
        'L-13 5', // tail fan corner (bottom)
        'L-9 3', // rear body bottom
        'L-4.5 3', // wing root trailing edge
        'L-3 20', // leading edge sweeping out to the right wingtip
        'Q0 25 3 20', // wingtip curve
        'L4.5 3', // back to the shoulder
        'L6 3', // shoulder
        'L12 2', // head bottom
        'Z',
      ].join(' '),
    },
    'fill:var(--frame)',
  );
}
